import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const impactData = [{
  metric: "+45%",
  label: "Conversion Rate",
  quote: "Kaleigh's copy didn't just sound good—it completely transformed how our audience engages with our brand. The results speak for themselves.",
  author: "Elena Rodriguez",
  role: "Director, E-com Style",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/avatar_elena_rodriguez.png"
}, {
  metric: "3x",
  label: "Lead Volume",
  quote: "We were struggling to articulate our value proposition. Kaleigh translated our complex tech into clear, actionable messaging that drives leads.",
  author: "Mike Ross",
  role: "Founder, InsureNow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/avatar_mike_ross.png"
}, {
  metric: "10k+",
  label: "Monthly Readers",
  quote: "Her content strategy took our blog from a ghost town to a thriving hub of industry authority. She truly understands the B2B landscape.",
  author: "Sarah Jenkins",
  role: "CMO, TechFlow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/avatar_sarah_jenkins.png"
}, {
  metric: "Top 3",
  label: "Search Rankings",
  quote: "Finally, a writer who understands SEO without sacrificing readability. Our organic traffic has never been higher.",
  author: "David Chen",
  role: "CEO, SaaSify",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/avatar_david_chen.png"
}, {
  metric: "200%",
  label: "ROI Increase",
  quote: "The investment in professional copywriting paid off within the first month. Kaleigh is a strategic partner, not just a writer.",
  author: "Amanda Lowery",
  role: "Owner, Boutique Digital",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/avatar_amanda_lowery.png"
}];
export default function TheImpactReport() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-impact-report" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10 mix-blend-overlay z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569965/img/ink_bleed_texture.png)] bg-cover bg-center bg-no-repeat" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-6">
            The Impact Report
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Real results for real businesses. See how clear, strategic messaging
            translates into measurable growth.
          </p>
        </div>

        <div className="px-4 md:px-12">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {impactData.map((item, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full p-1">
                    <Card className="h-full bg-card/40 border-border/30 backdrop-blur-sm hover:bg-card/60 transition-colors duration-300 flex flex-col justify-between">
                      <CardContent className="pt-8 pb-4">
                        <div className="mb-6">
                          <span className="block text-5xl md:text-6xl font-serif font-bold text-[var(--chart-2)] mb-2">
                            {item.metric}
                          </span>
                          <span className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                            {item.label}
                          </span>
                        </div>
                        <Quote className="size-8 text-[var(--chart-2)]/50 mb-4" />
                        <p className="text-lg text-foreground/90 italic leading-relaxed font-serif">
                          &ldquo;{item.quote}&rdquo;
                        </p>
                      </CardContent>
                      <CardFooter className="border-t border-border/30 pt-6 mt-auto">
                        <div className="flex items-center gap-4">
                          <SafeHydrate><Avatar className="size-12 border border-border/50">
                            <AvatarImage src={item.image} alt={item.author} className="object-cover" />
                            <AvatarFallback className="bg-[var(--primary)] text-[var(--primary-foreground)]">
                              {item.author.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <p className="font-semibold text-foreground text-sm">
                              {item.author}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {item.role}
                            </p>
                          </div>
                        </div>
                      </CardFooter>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 bg-background/80 border-border/50 hover:bg-[var(--chart-2)] hover:text-background hover:border-[var(--chart-2)] transition-all" />
            <CarouselNext className="hidden md:flex -right-12 bg-background/80 border-border/50 hover:bg-[var(--chart-2)] hover:text-background hover:border-[var(--chart-2)] transition-all" />
          </Carousel>
        </div>
      </div>
    </section>;
}